// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { InternalDocumentTypeComponent } from './internal-document-type/internal-document-type.component';
import { AllDealsComponent } from '../pddj/deals/all-deals/all-deals.component';
import { DealerFormComponent } from '../pddj/deals/create-deal/dealer-form/dealer-form.component';
import { DealCardComponent } from '../pddj/deals/deal-card/deal-card.component';
import { MaterialModule } from './material.module';
import { CameraCaptureComponent } from './camera-capture/camera-capture.component';
import { PDFViewComponent } from './pdf-view/pdf-view.component';
import { PhoneMaskDirective } from '../directives/phoneMask.directive';
import { SignatureCaptureComponent } from './signature-capture/signature-capture.component';
import { SignatureFormComponent } from './signature-form/signature-form.component';
import { VehicleInfoHeaderComponent } from './vehicle-info-header/vehicle-info-header.component';
import { ProfileInfoFormComponent } from './profile-info-form/profile-info-form.component';
import { AcceptTermsComponent } from './accept-terms/accept-terms.component';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { DealerComponent } from './dealer/dealer.component';
import { NavItemComponent } from './nav-item/nav-item.component';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { VehicleFormComponent } from './vehicle-form-component/vehicle-form.component';
import { VehicleComponent } from './vehicle/vehicle.component';
import { DdjPDFSpinnerComponent } from './loaders/ddj-pdf-spinner/ddj-pdf-spinner.component';
import { ProfileSectionComponent } from './profile-section/profile-section.component';
import { AgreementsDisclosuresComponent } from './agreements-disclosures/agreements-disclosures.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { DdjSpinnerComponent } from './loaders/ddj-spinner/ddj-spinner.component';
import { DdjLicenseSpinnerComponent } from './loaders/ddj-license-spinner/ddj-license-spinner.component';
import { ThreeDotLoaderComponent } from './loaders/three-dot-loader/three-dot-loader.component';
import { PipesModule } from '../pipes/pipes.module';
import { CreateDealSpinnerComponent } from './loaders/create-deal-spinner/create-deal-spinner.component';
import { UploadImageSpinnerComponent } from './loaders/upload-image-spinner/upload-image-spinner.component';
import { FirstLoginSignatureSpinnerComponent } from './loaders/first-login-signature-spinner/first-login-signature-spinner.component';
import { SystemMessageBannerComponent } from './system-message-banner/system-message-banner.component';
import { PasswordValidatorDirective } from '../directives/password-validator.directive';
import { ProfileNotificationsComponent } from './profile-notifications/profile-notifications.component';
import { EmptyMessageComponent } from './empty-message/empty-message.component';
import { HeaderComponent } from './general-layout/header/header.component';
import { FooterComponent } from './general-layout/footer/footer.component';
import { VehicleEditComponent } from './vehicle-edit-component/vehicle-edit.component';
import { InfoCardComponent } from './info-card/info-card.component';
import { InfoDetailComponent } from './info-card/info-detail/info-detail.component';
import { NeedHelpPanelComponent } from './need-help-panel/need-help-panel.component';
import { StorageJacketHelpPanelComponent } from './help-panels/storage-jacket-help/storage-jacket-help-panel.component';
import { LeadsHelpPanelComponent } from './help-panels/leads-help/leads-help-panel.component';
import { IntellicheckUploadComponent } from './intellicheck-upload/intellicheck-upload.component';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { IntellicheckLicenseFrontComponent } from './intellicheck-upload/license-front/intellicheck-license-front.component';
import { IntellicheckLicenseBackComponent } from './intellicheck-upload/license-back/intellicheck-license-back.component';
import { IntellicheckPassportBookletComponent } from './intellicheck-upload/passport-booklet/intellicheck-passport-booklet.component';
import { FillEmptyFieldDirective } from '../directives/fill-empty-field.directive';
import { ReplaceLineBreaksDirective } from '../directives/replace-line-breaks.directive';
import { ActiveInternalDocumentPipe } from '../pipes/active-internal-document.pipe';
import { ActiveInternalDocumentTypePipe } from '../pipes/active-internal-document-type.pipe';
import { IntellicheckLicenseSelfieComponent } from './intellicheck-upload/intellicheck-license-selfie/intellicheck-license-selfie.component';
import { ChangePasswordComponent } from './password/password.component';
import { EnterVerificationComponent } from '../login/two-factor-auth/enter-verification/enter-verification.component';
import { AdminGettingStartedComponent } from './admin-getting-started/admin-getting-started.component';
import { StoreDocumentsHelpPanelComponent } from './help-panels/store-documents-help/store-documents-help-panel.component';
import { DocStorageHelpPanelComponent } from './help-panels/doc-storage-help/doc-storage-help-panel.component';
import { DealerManagementHelpPanelComponent } from './help-panels/dealer-management-help/dealer-management-help-panel.component';
import { UserManagmeentHelpComponent } from './help-panels/user-management-help/user-management-help.component';
import { EditUserHelpPanelComponent } from './help-panels/edit-user-help/edit-user-help-panel.component';
import { StartDealJacketHelpComponent } from './help-panels/start-deal-jacket-help/start-deal-jacket-help.component';
import { ExpressUploadHelpComponent } from './help-panels/express-upload-help/express-upload-help.component';
import { ConsumerDocumentsHelpComponent } from './help-panels/consumer-documents-help/consumer-documents-help.component';
import { DocumentJacketHelpComponent } from './help-panels/document-jacket-help/document-jacket-help.component';
import { CustomDealTypesHelpPanelComponent } from './help-panels/custom-deal-types-help/custom-deal-types-help-panel.component';
import { YourDealHelpComponent } from './help-panels/your-deal-help-panel/your-deal-help.component';
import { DealsHelpPanelComponent } from './help-panels/deals-help/deals-help-panel.component';
import { DynamsoftScanComponent } from './intellicheck-upload/dynamsoft-scan/dynamsoft-scan.component';
import { FAndIHelpPanelComponent } from './help-panels/f-and-i-help/f-and-i-help-panel.component';

@NgModule({
    declarations: [
        AcceptTermsComponent,
        AllDealsComponent,
        BreadcrumbComponent,
        CameraCaptureComponent,
        DealCardComponent,
        DealerComponent,
        ChangePasswordComponent,
        DealerFormComponent,
        NavItemComponent,
        PDFViewComponent,
        PhoneMaskDirective,
        ProfileNotificationsComponent,
        ProfileInfoFormComponent,
        ProfileSectionComponent,
        SearchBarComponent,
        SignatureFormComponent,
        SignatureCaptureComponent,
        InternalDocumentTypeComponent,
        VehicleComponent,
        VehicleEditComponent,
        VehicleFormComponent,
        VehicleInfoHeaderComponent,
        DdjPDFSpinnerComponent,
        DdjLicenseSpinnerComponent,
        AgreementsDisclosuresComponent,
        FileUploadComponent,
        DdjSpinnerComponent,
        ThreeDotLoaderComponent,
        CreateDealSpinnerComponent,
        UploadImageSpinnerComponent,
        FirstLoginSignatureSpinnerComponent,
        EmptyMessageComponent,
        SystemMessageBannerComponent,
        PasswordValidatorDirective,
        HeaderComponent,
        FooterComponent,
        InfoCardComponent,
        InfoDetailComponent,
        NeedHelpPanelComponent,
        IntellicheckUploadComponent,
        IntellicheckLicenseFrontComponent,
        IntellicheckLicenseBackComponent,
        IntellicheckLicenseSelfieComponent,
        IntellicheckPassportBookletComponent,
        DynamsoftScanComponent,
        FillEmptyFieldDirective,
        ReplaceLineBreaksDirective,
        ActiveInternalDocumentPipe,
        ActiveInternalDocumentTypePipe,
        EnterVerificationComponent,
        LeadsHelpPanelComponent,
        AdminGettingStartedComponent,
        StorageJacketHelpPanelComponent,
        StoreDocumentsHelpPanelComponent,
        DocStorageHelpPanelComponent,
        DealerManagementHelpPanelComponent,
        UserManagmeentHelpComponent,
        EditUserHelpPanelComponent,
        StartDealJacketHelpComponent,
        ExpressUploadHelpComponent,
        ConsumerDocumentsHelpComponent,
        DocumentJacketHelpComponent,
        CustomDealTypesHelpPanelComponent,
        StoreDocumentsHelpPanelComponent,
        YourDealHelpComponent,
        DealsHelpPanelComponent,
        FAndIHelpPanelComponent,
    ],
    imports: [CommonModule, FormsModule, MaterialModule, ReactiveFormsModule, RouterModule, PipesModule],
    exports: [
        AcceptTermsComponent,
        AllDealsComponent,
        BreadcrumbComponent,
        CameraCaptureComponent,
        DealCardComponent,
        DealerComponent,
        ChangePasswordComponent,
        DealerFormComponent,
        NavItemComponent,
        PDFViewComponent,
        PhoneMaskDirective,
        ProfileNotificationsComponent,
        ProfileInfoFormComponent,
        ProfileSectionComponent,
        SearchBarComponent,
        SignatureFormComponent,
        SignatureCaptureComponent,
        InternalDocumentTypeComponent,
        VehicleComponent,
        VehicleEditComponent,
        VehicleFormComponent,
        VehicleInfoHeaderComponent,
        VehicleInfoHeaderComponent,
        AgreementsDisclosuresComponent,
        FileUploadComponent,
        DdjPDFSpinnerComponent,
        DdjSpinnerComponent,
        ThreeDotLoaderComponent,
        CreateDealSpinnerComponent,
        UploadImageSpinnerComponent,
        FirstLoginSignatureSpinnerComponent,
        EmptyMessageComponent,
        SystemMessageBannerComponent,
        PasswordValidatorDirective,
        HeaderComponent,
        FooterComponent,
        InfoCardComponent,
        InfoDetailComponent,
        DdjLicenseSpinnerComponent,
        NeedHelpPanelComponent,
        LeadsHelpPanelComponent,
        IntellicheckUploadComponent,
        FillEmptyFieldDirective,
        ReplaceLineBreaksDirective,
        ActiveInternalDocumentPipe,
        ActiveInternalDocumentTypePipe,
        EnterVerificationComponent,
        AdminGettingStartedComponent,
        StorageJacketHelpPanelComponent,
        StoreDocumentsHelpPanelComponent,
        DocStorageHelpPanelComponent,
        DealerManagementHelpPanelComponent,
        UserManagmeentHelpComponent,
        EditUserHelpPanelComponent,
        StartDealJacketHelpComponent,
        ExpressUploadHelpComponent,
        ConsumerDocumentsHelpComponent,
        DocumentJacketHelpComponent,
        CustomDealTypesHelpPanelComponent,
        StoreDocumentsHelpPanelComponent,
        DealsHelpPanelComponent,
        FAndIHelpPanelComponent,
    ],
    providers: [ActiveInternalDocumentTypePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
